/**
 * Notification Settings admin page
 * Configure the client renewal reminder schedule.
 *
 * Company Info and Deleted Task Retention used to live here too; neither is a
 * notification setting, so both moved to General Settings (`/admin/settings`).
 *
 * NOTE ON THE SHARED ENDPOINT: this page and General Settings read and write the
 * SAME admin settings row (`GET`/`PUT /api/v1/admin/notifications`). The PUT replaces
 * the whole row and `company_name` is required, so this page must round-trip the
 * General Settings fields untouched — hence `loaded` below.
 */
import { useState, useEffect } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { useAuth } from '../../services/auth'
import { getNotificationSettings, updateNotificationSettings } from '../../services/admin'
import type { NotificationSettings } from '../../types'

const PRESET_BEFORE = [90, 60, 30, 14, 7, 3]
const PRESET_AFTER = [7, 14, 30, 60]

export default function NotificationSettingsPage() {
  const { user } = useAuth()

  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [saveSuccess, setSaveSuccess] = useState(false)

  // Full settings as last loaded — the source for the fields this page does not edit.
  const [loaded, setLoaded] = useState<NotificationSettings | null>(null)

  // Form state
  const [enabled, setEnabled] = useState(false)
  /** Loaded with the settings: how many queued messages are already past due. */
  const [pendingDue, setPendingDue] = useState(0)
  const [beforeDays, setBeforeDays] = useState<number[]>([90, 60, 30, 14, 7])
  const [sendOverdue, setSendOverdue] = useState(false)
  const [afterDays, setAfterDays] = useState<number[]>([7, 30])

  useEffect(() => {
    load()
  }, [])

  async function load() {
    setLoading(true)
    setLoadError('')
    try {
      const s = await getNotificationSettings()
      setLoaded(s)
      setEnabled(s.notification_enabled)
      setPendingDue(s.pending_due_count ?? 0)
      setBeforeDays(s.reminder_days_before)
      setSendOverdue(s.send_overdue_reminders)
      setAfterDays(s.overdue_reminder_days_after)
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load settings')
    } finally {
      setLoading(false)
    }
  }

  function toggleDay(days: number[], setDays: (d: number[]) => void, day: number) {
    if (days.includes(day)) {
      setDays(days.filter((d) => d !== day))
    } else {
      setDays([...days, day].sort((a, b) => b - a))
    }
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault()
    setSaveError('')
    setSaveSuccess(false)
    setSaving(true)
    try {
      const payload: Partial<NotificationSettings> = {
        // Owned by this page.
        notification_enabled: enabled,
        reminder_days_before: beforeDays,
        send_overdue_reminders: sendOverdue,
        overdue_reminder_days_after: afterDays,
        // Carried through untouched — owned by the General Settings page.
        company_name: loaded?.company_name ?? 'FCC License Manager',
        company_email: loaded?.company_email ?? null,
        company_phone: loaded?.company_phone ?? null,
        company_logo_url: loaded?.company_logo_url ?? null,
        company_website: loaded?.company_website ?? null,
        task_deleted_retention_days: loaded?.task_deleted_retention_days ?? null,
      }
      const updated = await updateNotificationSettings(payload)
      setLoaded(updated)
      setSaveSuccess(true)
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save settings')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Automatic Reminders</h2>
        <div className="text-gray-500">Loading…</div>
      </div>
    )
  }

  if (!user?.is_superuser) {
    return <Navigate to="/dashboard" replace />
  }

  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-4">Automatic Reminders</h2>

      {loadError && <div className="alert alert-error mb-6">{loadError}</div>}

      <form onSubmit={handleSave} className="space-y-6">

        {/* Global toggle */}
        <div className="card">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="enabled"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="h-6 w-6"
            />
            <label htmlFor="enabled" className="text-base font-medium text-gray-900">
              Enable client renewal reminder emails
            </label>
          </div>
          <p className="mt-1 text-sm text-gray-500 ml-7">
            When enabled, renewal reminder emails go out to clients automatically each day, at the
            intervals set below.
          </p>

          {/* Switching this on releases a pause, and a pause that lasted a while holds
              a backlog. Shown BEFORE saving, because the surprise it prevents is
              finding out afterwards: Jesse enabled reminders and a stack of backdated
              messages went out at once, with nothing having said any were waiting. */}
          {enabled && pendingDue > 0 && (
            <div
              role="alert"
              className="mt-3 ml-7 rounded border border-amber-300 bg-amber-50 p-2.5 text-sm text-amber-800"
            >
              <strong>
                {pendingDue} queued message{pendingDue === 1 ? ' is' : 's are'} already
                past due.
              </strong>{' '}
              Saving with this on will send {pendingDue === 1 ? 'it' : 'them'} within
              about 15 minutes. Review the{' '}
              <Link to="/admin/email?tab=outbox" className="underline font-medium">
                Outbox
              </Link>{' '}
              first if you are not sure what is waiting — anything more than a week
              overdue is held for review rather than sent.
            </div>
          )}
        </div>

        {/* Reminder schedule */}
        <div className="card">
          <h2 className="text-xl font-semibold mb-1">Renewal Reminder Schedule</h2>
          <p className="text-sm text-gray-500 mb-4">
            Send a reminder N days <strong>before</strong> license expiration. Check all that apply.
          </p>
          <div className="flex flex-wrap gap-3">
            {PRESET_BEFORE.map((day) => (
              <label key={day} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={beforeDays.includes(day)}
                  onChange={() => toggleDay(beforeDays, setBeforeDays, day)}
                  className="h-6 w-6"
                />
                <span className="text-sm text-gray-700">{day} days</span>
              </label>
            ))}
          </div>
          {beforeDays.length === 0 && (
            <p className="mt-2 text-sm text-amber-600">No reminders selected — no pre-expiration emails will be sent.</p>
          )}
        </div>

        {/* Overdue reminders */}
        <div className="card">
          <div className="flex items-center gap-3 mb-3">
            <input
              type="checkbox"
              id="sendOverdue"
              checked={sendOverdue}
              onChange={(e) => setSendOverdue(e.target.checked)}
              className="h-6 w-6"
            />
            <label htmlFor="sendOverdue" className="text-base font-medium text-gray-900">
              Send overdue reminders
            </label>
          </div>
          <p className="text-sm text-gray-500 mb-4 ml-7">
            Send a follow-up email N days <strong>after</strong> license expiration.
          </p>
          {sendOverdue && (
            <div className="flex flex-wrap gap-3 ml-7">
              {PRESET_AFTER.map((day) => (
                <label key={day} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={afterDays.includes(day)}
                    onChange={() => toggleDay(afterDays, setAfterDays, day)}
                    className="h-6 w-6"
                  />
                  <span className="text-sm text-gray-700">{day} days after</span>
                </label>
              ))}
            </div>
          )}
        </div>

        {saveError && <div className="alert alert-error">{saveError}</div>}
        {saveSuccess && <div className="alert alert-success">Settings saved successfully.</div>}

        <div>
          {/* Saving without a successful load would write the General Settings fields
              back as defaults, silently wiping company info. Block it instead. */}
          <button type="submit" disabled={saving || !loaded} className="btn btn-primary">
            {saving ? 'Saving…' : 'Save Settings'}
          </button>
        </div>
      </form>
    </div>
  )
}
